/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin CMS Blog & Category Management View (Task 3.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Newspaper,
  FolderTree,
  Plus,
  RefreshCw,
  Search,
  Edit2,
  Trash2,
  FileText,
  X,
  Check,
  AlertTriangle,
  Globe,
  Calendar,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { DataTable, type ColumnDef } from '../components/table/DataTable';
import type {
  CmsPost,
  CreateCmsPostInput,
  UpdateCmsPostInput,
  CmsCategory,
  CreateCmsCategoryInput,
  UpdateCmsCategoryInput,
} from '@sofia/shared-types';

export const CmsBlogManagementView: React.FC = () => {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'posts' | 'categories'>('posts');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [localeFilter, setLocaleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modals state
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<CmsPost | null>(null);
  const [deletingPost, setDeletingPost] = useState<CmsPost | null>(null);

  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CmsCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<CmsCategory | null>(null);

  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Queries
  const { data: categories = [], isLoading: isLoadingCategories } = useQuery<CmsCategory[]>({
    queryKey: ['admin-cms-categories'],
    queryFn: async () => {
      const res = await adminClient.get('/api/v1/admin/cms/categories');
      return res.data || [];
    },
  });

  const { data: postsData, isLoading: isLoadingPosts, refetch: refetchPosts } = useQuery<{
    items: CmsPost[];
    total: number;
  }>({
    queryKey: ['admin-cms-posts', categoryFilter, localeFilter, statusFilter, searchQuery],
    queryFn: async () => {
      const params: Record<string, any> = { limit: 100 };
      if (categoryFilter !== 'all') params.categoryId = categoryFilter;
      if (localeFilter !== 'all') params.locale = localeFilter;
      if (statusFilter !== 'all') params.isPublished = statusFilter === 'published';
      if (searchQuery.trim().length > 0) params.search = searchQuery.trim();

      const res = await adminClient.get('/api/v1/admin/cms/posts', params);
      return res.data || { items: [], total: 0 };
    },
  });

  const postsList = postsData?.items || [];

  // Mutations
  const createPostMutation = useMutation({
    mutationFn: (payload: CreateCmsPostInput) => adminClient.post('/api/v1/admin/cms/posts', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-posts'] });
      setIsPostModalOpen(false);
      showToast('success', t('admin.cms.postCreated') || 'Article published successfully.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to create article.');
    },
  });

  const updatePostMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCmsPostInput }) =>
      adminClient.patch(`/api/v1/admin/cms/posts/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-posts'] });
      setIsPostModalOpen(false);
      setEditingPost(null);
      showToast('success', t('admin.cms.postUpdated') || 'Article updated successfully.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to update article.');
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: (id: string) => adminClient.delete(`/api/v1/admin/cms/posts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-posts'] });
      setDeletingPost(null);
      showToast('success', t('admin.cms.postDeleted') || 'Article deleted successfully.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to delete article.');
    },
  });

  const createCatMutation = useMutation({
    mutationFn: (payload: CreateCmsCategoryInput) => adminClient.post('/api/v1/admin/cms/categories', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-categories'] });
      setIsCatModalOpen(false);
      showToast('success', t('admin.cms.categoryCreated') || 'Category created successfully.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to create category.');
    },
  });

  const updateCatMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCmsCategoryInput }) =>
      adminClient.patch(`/api/v1/admin/cms/categories/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-categories'] });
      setIsCatModalOpen(false);
      setEditingCategory(null);
      showToast('success', t('admin.cms.categoryUpdated') || 'Category updated successfully.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to update category.');
    },
  });

  const deleteCatMutation = useMutation({
    mutationFn: (id: string) => adminClient.delete(`/api/v1/admin/cms/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-categories'] });
      setDeletingCategory(null);
      showToast('success', t('admin.cms.categoryDeleted') || 'Category deleted successfully.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to delete category.');
    },
  });

  // Table Columns for Posts
  const postColumns = useMemo<ColumnDef<CmsPost>[]>(() => [
    {
      id: 'title',
      header: t('admin.cms.colTitle') || 'Article Title',
      accessorKey: 'title',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {row.coverImageUrl ? (
            <img
              src={row.coverImageUrl}
              alt=""
              className="w-10 h-10 rounded-lg object-cover border border-surface-border shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-surface-elevated border border-surface-border flex items-center justify-center text-muted-fg shrink-0">
              <FileText className="w-4 h-4" />
            </div>
          )}
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-xs text-foreground truncate max-w-xs">{row.title}</span>
            <span className="text-[10px] text-muted-fg font-mono truncate max-w-xs">/blog/{row.slug}</span>
          </div>
        </div>
      ),
    },
    {
      id: 'category',
      header: t('admin.cms.colCategory') || 'Category',
      accessorKey: 'categoryName',
      cell: ({ row }) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-primary/10 text-primary border border-primary/20">
          {row.categoryName || 'General'}
        </span>
      ),
    },
    {
      id: 'locale',
      header: t('admin.cms.colLocale') || 'Locale',
      accessorKey: 'locale',
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase font-semibold text-muted-fg bg-surface-elevated px-2 py-0.5 rounded border border-surface-border">
          <Globe className="w-3 h-3 text-muted-fg/70" />
          {row.locale}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('admin.cms.colStatus') || 'Status',
      accessorKey: 'isPublished',
      cell: ({ row }) => (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
            row.isPublished
              ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
              : 'bg-amber-500/10 text-amber-500 border-amber-500/20'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${row.isPublished ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          {row.isPublished ? (t('admin.cms.statusPublished') || 'Published') : (t('admin.cms.statusDraft') || 'Draft')}
        </span>
      ),
    },
    {
      id: 'date',
      header: t('admin.cms.colDate') || 'Updated',
      accessorKey: 'updatedAt',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-[11px] text-muted-fg font-mono">
          <Calendar className="w-3 h-3 opacity-60" />
          {new Date(row.updatedAt * 1000).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => {
              setEditingPost(row);
              setIsPostModalOpen(true);
            }}
            className="p-1.5 text-muted-fg hover:text-foreground hover:bg-surface-elevated rounded-lg transition-colors"
            title="Edit Article"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setDeletingPost(row)}
            className="p-1.5 text-muted-fg hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
            title="Delete Article"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ], [t]);

  // Table Columns for Categories
  const categoryColumns = useMemo<ColumnDef<CmsCategory>[]>(() => [
    {
      id: 'name',
      header: t('admin.cms.colName') || 'Category Name',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-semibold text-xs text-foreground">{row.name}</span>
          <span className="text-[10px] text-muted-fg font-mono">/category/{row.slug}</span>
        </div>
      ),
    },
    {
      id: 'description',
      header: t('admin.cms.colDescription') || 'Description',
      accessorKey: 'description',
      cell: ({ row }) => (
        <span className="text-xs text-muted-fg line-clamp-1">{row.description || '—'}</span>
      ),
    },
    {
      id: 'sortOrder',
      header: t('admin.cms.colOrder') || 'Order',
      accessorKey: 'sortOrder',
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-fg">#{row.sortOrder}</span>
      ),
    },
    {
      id: 'isActive',
      header: t('admin.cms.colStatus') || 'Status',
      accessorKey: 'isActive',
      cell: ({ row }) => (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border ${
            row.isActive
              ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
              : 'bg-surface-elevated text-muted-fg border-surface-border'
          }`}
        >
          {row.isActive ? 'Active' : 'Disabled'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => {
              setEditingCategory(row);
              setIsCatModalOpen(true);
            }}
            className="p-1.5 text-muted-fg hover:text-foreground hover:bg-surface-elevated rounded-lg transition-colors"
            title="Edit Category"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setDeletingCategory(row)}
            className="p-1.5 text-muted-fg hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
            title="Delete Category"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ], [t]);

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border shadow-xl text-xs font-semibold backdrop-blur-md animate-fade-in ${
            toastMessage.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
              : 'bg-destructive/10 border-destructive/30 text-destructive'
          }`}
        >
          {toastMessage.type === 'success' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header & Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-surface-border">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-heading font-bold text-foreground">
              {t('admin.cms.title') || 'Blog & Content Hub'}
            </h2>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-primary/10 text-primary border border-primary/20">
              CMS V2
            </span>
          </div>
          <p className="text-xs text-muted-fg mt-1">
            {t('admin.cms.subtitle') || 'Author, localize, and manage public SEO articles and knowledge categories.'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Sub Tab Switcher */}
          <div className="p-1 rounded-xl bg-surface-elevated border border-surface-border flex items-center gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('posts')}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                activeTab === 'posts'
                  ? 'bg-primary text-white shadow-sm font-semibold'
                  : 'text-muted-fg hover:text-foreground'
              }`}
            >
              <Newspaper className="w-3.5 h-3.5" />
              <span>{t('admin.cms.tabArticles') || 'Articles'}</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-white/20">
                {postsData?.total ?? 0}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('categories')}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                activeTab === 'categories'
                  ? 'bg-primary text-white shadow-sm font-semibold'
                  : 'text-muted-fg hover:text-foreground'
              }`}
            >
              <FolderTree className="w-3.5 h-3.5" />
              <span>{t('admin.cms.tabCategories') || 'Categories'}</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-white/20">
                {categories.length}
              </span>
            </button>
          </div>

          {activeTab === 'posts' ? (
            <button
              type="button"
              onClick={() => {
                setEditingPost(null);
                setIsPostModalOpen(true);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-white font-medium text-xs hover:bg-primary/90 shadow-sm transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>{t('admin.cms.btnNewArticle') || 'New Article'}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setEditingCategory(null);
                setIsCatModalOpen(true);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-white font-medium text-xs hover:bg-primary/90 shadow-sm transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>{t('admin.cms.btnNewCategory') || 'New Category'}</span>
            </button>
          )}
        </div>
      </div>

      {activeTab === 'posts' ? (
        <>
          {/* Posts Filter Toolbar */}
          <div className="p-3 rounded-xl bg-surface border border-surface-border flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 text-muted-fg absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('admin.cms.searchPlaceholder') || 'Search articles by title or slug...'}
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-primary/50"
              />
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-2.5 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
            >
              <option value="all">{t('admin.cms.filterAllCategories') || 'All Categories'}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <select
              value={localeFilter}
              onChange={(e) => setLocaleFilter(e.target.value)}
              className="px-2.5 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
            >
              <option value="all">{t('admin.cms.filterAllLocales') || 'All Locales'}</option>
              <option value="en">English (en)</option>
              <option value="vi">Tiếng Việt (vi)</option>
              <option value="ja">日本語 (ja)</option>
              <option value="ko">한국어 (ko)</option>
              <option value="es">Español (es)</option>
              <option value="zh">中文 (zh)</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2.5 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
            >
              <option value="all">{t('admin.cms.filterAllStatuses') || 'All Statuses'}</option>
              <option value="published">{t('admin.cms.statusPublished') || 'Published'}</option>
              <option value="draft">{t('admin.cms.statusDraft') || 'Draft'}</option>
            </select>

            <button
              type="button"
              onClick={() => refetchPosts()}
              className="p-1.5 rounded-lg border border-surface-border hover:bg-surface-elevated text-muted-fg hover:text-foreground transition-colors"
              title="Refresh Articles"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Posts DataTable */}
          <div className="bg-surface rounded-2xl border border-surface-border overflow-hidden">
            <DataTable columns={postColumns} data={postsList} isLoading={isLoadingPosts} />
          </div>
        </>
      ) : (
        /* Categories Table */
        <div className="bg-surface rounded-2xl border border-surface-border overflow-hidden">
          <DataTable columns={categoryColumns} data={categories} isLoading={isLoadingCategories} />
        </div>
      )}

      {/* Post Create / Edit Modal */}
      {isPostModalOpen && (
        <PostModal
          post={editingPost}
          categories={categories}
          isOpen={isPostModalOpen}
          onClose={() => {
            setIsPostModalOpen(false);
            setEditingPost(null);
          }}
          onSave={(payload) => {
            if (editingPost) {
              updatePostMutation.mutate({ id: editingPost.id, payload });
            } else {
              createPostMutation.mutate(payload as CreateCmsPostInput);
            }
          }}
          isSubmitting={createPostMutation.isPending || updatePostMutation.isPending}
        />
      )}

      {/* Category Create / Edit Modal */}
      {isCatModalOpen && (
        <CategoryModal
          category={editingCategory}
          isOpen={isCatModalOpen}
          onClose={() => {
            setIsCatModalOpen(false);
            setEditingCategory(null);
          }}
          onSave={(payload) => {
            if (editingCategory) {
              updateCatMutation.mutate({ id: editingCategory.id, payload });
            } else {
              createCatMutation.mutate(payload as CreateCmsCategoryInput);
            }
          }}
          isSubmitting={createCatMutation.isPending || updateCatMutation.isPending}
        />
      )}

      {/* Delete Post Confirm */}
      {deletingPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm rounded-2xl bg-surface border border-surface-border p-6 space-y-4 shadow-2xl">
            <div className="flex items-center gap-3 text-destructive">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <h3 className="text-sm font-heading font-semibold text-foreground">
                {t('admin.cms.confirmDeletePost') || 'Delete Article?'}
              </h3>
            </div>
            <p className="text-xs text-muted-fg leading-relaxed">
              Are you sure you want to delete <span className="font-semibold text-foreground">"{deletingPost.title}"</span>? This action is recorded in immutable audit logs.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeletingPost(null)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
              >
                {t('admin.common.cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                disabled={deletePostMutation.isPending}
                onClick={() => deletePostMutation.mutate(deletingPost.id)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-destructive text-white hover:bg-destructive/90 shadow-sm disabled:opacity-50"
              >
                {deletePostMutation.isPending ? 'Deleting...' : (t('admin.common.delete') || 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Confirm */}
      {deletingCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm rounded-2xl bg-surface border border-surface-border p-6 space-y-4 shadow-2xl">
            <div className="flex items-center gap-3 text-destructive">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <h3 className="text-sm font-heading font-semibold text-foreground">
                {t('admin.cms.confirmDeleteCategory') || 'Delete Category?'}
              </h3>
            </div>
            <p className="text-xs text-muted-fg leading-relaxed">
              Delete category <span className="font-semibold text-foreground">"{deletingCategory.name}"</span>? Articles under this category will be unassigned.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeletingCategory(null)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
              >
                {t('admin.common.cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                disabled={deleteCatMutation.isPending}
                onClick={() => deleteCatMutation.mutate(deletingCategory.id)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-destructive text-white hover:bg-destructive/90 shadow-sm disabled:opacity-50"
              >
                {deleteCatMutation.isPending ? 'Deleting...' : (t('admin.common.delete') || 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Post Create / Edit Modal Component
// ============================================================================

interface PostModalProps {
  post: CmsPost | null;
  categories: CmsCategory[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: any) => void;
  isSubmitting: boolean;
}

const PostModal: React.FC<PostModalProps> = ({
  post,
  categories,
  isOpen,
  onClose,
  onSave,
  isSubmitting,
}) => {
  const [title, setTitle] = useState(post?.title || '');
  const [slug, setSlug] = useState(post?.slug || '');
  const [categoryId, setCategoryId] = useState(post?.categoryId || '');
  const [locale, setLocale] = useState(post?.locale || 'en');
  const [summary, setSummary] = useState(post?.summary || '');
  const [coverImageUrl, setCoverImageUrl] = useState(post?.coverImageUrl || '');
  const [content, setContent] = useState(post?.content || '');
  const [isPublished, setIsPublished] = useState(post ? post.isPublished : true);
  const [previewMode, setPreviewMode] = useState<'edit' | 'preview'>('edit');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      title,
      slug: slug.trim() ? slug.trim() : undefined,
      categoryId: categoryId || undefined,
      locale,
      summary: summary.trim() ? summary.trim() : undefined,
      coverImageUrl: coverImageUrl.trim() ? coverImageUrl.trim() : undefined,
      content,
      isPublished,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="w-full max-w-3xl my-8 rounded-2xl bg-surface border border-surface-border shadow-2xl flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border">
          <div className="flex items-center gap-2">
            <Newspaper className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-sm text-foreground">
              {post ? 'Edit Article' : 'Compose New Article'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                Article Title *
              </label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Announcing Agentic Workspaces 2.0"
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                URL Slug (Optional, auto-generated)
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g. announcing-agentic-workspaces"
                className="w-full px-3 py-2 text-xs font-mono rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                Category
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="">General / Uncategorized</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                Language / Locale
              </label>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="en">English (en)</option>
                <option value="vi">Tiếng Việt (vi)</option>
                <option value="ja">日本語 (ja)</option>
                <option value="ko">한국어 (ko)</option>
                <option value="es">Español (es)</option>
                <option value="zh">中文 (zh)</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                Cover Image URL
              </label>
              <input
                type="url"
                value={coverImageUrl}
                onChange={(e) => setCoverImageUrl(e.target.value)}
                placeholder="https://images.unsplash.com/..."
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                SEO Teaser Summary
              </label>
              <textarea
                rows={2}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="A concise summary for meta description and social sharing cards..."
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Content Editor with Markdown / Preview */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="block text-[11px] font-semibold text-muted-fg">
                Article Body (Markdown format) *
              </label>
              <div className="flex items-center gap-1 p-0.5 rounded-lg bg-surface-elevated border border-surface-border text-[11px]">
                <button
                  type="button"
                  onClick={() => setPreviewMode('edit')}
                  className={`px-2 py-0.5 rounded ${previewMode === 'edit' ? 'bg-primary text-white font-medium' : 'text-muted-fg'}`}
                >
                  Write
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewMode('preview')}
                  className={`px-2 py-0.5 rounded ${previewMode === 'preview' ? 'bg-primary text-white font-medium' : 'text-muted-fg'}`}
                >
                  Preview
                </button>
              </div>
            </div>

            {previewMode === 'edit' ? (
              <textarea
                required
                rows={12}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="# Introduction&#10;&#10;Write your article body in Markdown..."
                className="w-full px-3 py-2 text-xs font-mono rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            ) : (
              <div className="w-full min-h-[240px] max-h-[300px] overflow-y-auto p-4 rounded-lg bg-surface-elevated border border-surface-border text-xs text-foreground prose prose-invert prose-xs max-w-none">
                <pre className="whitespace-pre-wrap font-sans">{content || 'No content written yet.'}</pre>
              </div>
            )}
          </div>

          {/* Publication State Switch */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated border border-surface-border">
            <div>
              <span className="text-xs font-semibold text-foreground">Publish State</span>
              <p className="text-[10px] text-muted-fg">Make this article immediately visible on the public blog.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-surface-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Article'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// Category Create / Edit Modal Component
// ============================================================================

interface CategoryModalProps {
  category: CmsCategory | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: any) => void;
  isSubmitting: boolean;
}

const CategoryModal: React.FC<CategoryModalProps> = ({
  category,
  isOpen,
  onClose,
  onSave,
  isSubmitting,
}) => {
  const [name, setName] = useState(category?.name || '');
  const [slug, setSlug] = useState(category?.slug || '');
  const [description, setDescription] = useState(category?.description || '');
  const [sortOrder, setSortOrder] = useState(category?.sortOrder ?? 0);
  const [isActive, setIsActive] = useState(category ? category.isActive : true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      slug: slug.trim() ? slug.trim() : undefined,
      description: description.trim() ? description.trim() : undefined,
      sortOrder: Number(sortOrder),
      isActive,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md rounded-2xl bg-surface border border-surface-border shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-surface-border pb-3">
          <div className="flex items-center gap-2">
            <FolderTree className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-sm text-foreground">
              {category ? 'Edit Category' : 'New Blog Category'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              Category Name *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Industry News"
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              Slug (Optional, auto-generated)
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="e.g. industry-news"
              className="w-full px-3 py-2 text-xs font-mono rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              Description
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description for category listings..."
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div className="flex flex-col justify-end">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="rounded border-surface-border text-primary focus:ring-primary"
                />
                <span className="text-xs text-foreground font-medium">Active Category</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
